// v2 fox — a lithe standing fox: dark slim legs (the "black stockings"), a
// low-slung body with a deep chest and cream bib, a darker saddle along the
// back, a sharp head with black-backed ears (pale inside) and a tapered muzzle,
// and the signature brush — a thick swept tail ending in a cream tip. Two
// coats: red fox and dark cross fox. Ported 1:1 from age-of-mcp's v2/fox.ts,
// using the library's shared `critter` (quadLegs) helper.
import * as THREE from "three";
import { defineModel, critter, type Part } from "../../lib";

const COATS = [
  { coat: "#c65f24", saddle: "#a8481a", bib: "#efe6d4", leg: "#3c2010", tip: "#f2ece0" },
  { coat: "#8a4a20", saddle: "#5e3414", bib: "#e0d6c4", leg: "#2a180c", tip: "#e8e0d0" },
];

function foxParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.045, dz: 0.02, bodyY: 0.062, rTop: 0.0055, rFoot: 0.0042, color: c.leg,
  });
  parts.push(
    // low-slung torso with a deep chest mass
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.coat,
      position: [0, 0.062, 0], scale: [1.75, 0.95, 0.8] },
    { geometry: new THREE.IcosahedronGeometry(0.027, 0), color: c.coat,
      position: [0.044, 0.06, 0], scale: [1.0, 1.05, 0.85] },
    // cream bib on the chest
    { geometry: new THREE.IcosahedronGeometry(0.0175, 0), color: c.bib,
      position: [0.06, 0.05, 0], scale: [0.9, 1.1, 0.8] },
    // darker saddle along the back
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.saddle,
      position: [-0.018, 0.077, 0], scale: [1.35, 0.6, 0.85] },
    // sharp head, tapered muzzle, black nose
    { geometry: new THREE.IcosahedronGeometry(0.021, 0), color: c.coat,
      position: [0.08, 0.091, 0], scale: [1.15, 0.95, 0.95] },
    { geometry: new THREE.ConeGeometry(0.0095, 0.03, 4), color: c.coat,
      position: [0.104, 0.084, 0], rotation: [0, 0, -Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.0035, 0), color: "#1c1410",
      position: [0.119, 0.084, 0] },
    // black-backed ears with pale inner cones
    { geometry: new THREE.ConeGeometry(0.009, 0.024, 4), color: c.leg,
      position: [0.073, 0.114, 0.0135], rotation: [0.2, 0, 0.08] },
    { geometry: new THREE.ConeGeometry(0.009, 0.024, 4), color: c.leg,
      position: [0.073, 0.114, -0.0135], rotation: [-0.2, 0, 0.08] },
    { geometry: new THREE.ConeGeometry(0.005, 0.014, 4), color: "#e0b89c",
      position: [0.0765, 0.111, 0.0125], rotation: [0.2, 0, 0.08] },
    { geometry: new THREE.ConeGeometry(0.005, 0.014, 4), color: "#e0b89c",
      position: [0.0765, 0.111, -0.0125], rotation: [-0.2, 0, 0.08] },
    // the brush — root, thick plume, cream tip, carried low and swept back
    { geometry: new THREE.CylinderGeometry(0.008, 0.015, 0.05, 5), color: c.coat,
      position: [-0.068, 0.058, 0], rotation: [0, 0, 1.15] },
    { geometry: new THREE.IcosahedronGeometry(0.0175, 0), color: c.saddle,
      position: [-0.099, 0.05, 0], scale: [1.6, 0.95, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.0105, 0), color: c.tip,
      position: [-0.123, 0.046, 0] },
  );
  return parts;
}

export const foxV2 = defineModel({
  id: "fox-v2",
  batchId: "fable",
  name: "Fox (v2)",
  tags: ["creature","fox","low-poly","age-of-mcp","fable"],
  summary: "Low-poly fox.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.1,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => foxParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => foxParts(1) },
  ],
  animations: [],
});
